import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useCallConnectingEventListener,
  useCallEndedEventListener,
  useCallStartedEventListener,
} from 'infrastructure/customEvents/callEvents';
import {
  getAgentState,
  getCallControlSnapshot,
  getTransferSnapshot,
} from 'services/connectService';

import type { ICallState } from 'components/organisms/connect/call-panel/types';

const TICK_MS = 1000;
const AGENT_STATE_POLL_MS = 5000;
// After a local hold/mute click, the SDK snapshot is ignored for this long.
// The agent-data snapshot lags the API call by a moment, so reconciling
// immediately would flick a just-clicked tile back before flipping it again.
const CONTROL_RECONCILE_HOLDOFF_MS = 4000;

const INITIAL_STATE: ICallState = {
  phase: 'idle',
  elapsedSeconds: 0,
  isHeld: false,
  heldSeconds: 0,
  isMuted: false,
  agentStateName: null,
  isAgentRoutable: false,
  transfer: null,
  failed: false,
  failureStateName: null,
};

const sameTransfer = (a: ICallState['transfer'], b: ICallState['transfer']) => {
  if (!a || !b) return a === b;
  return (
    a.name === b.name &&
    a.isConnected === b.isConnected &&
    a.isOnHold === b.isOnHold &&
    a.customerOnHold === b.customerOnHold
  );
};

/**
 * Turns the existing call events into the call panel's view model.
 *
 * Phase mapping matches what connectService actually distinguishes — there is no
 * after-call-work concept anywhere in the app, so 'ended' means "contact ENDED,
 * modal still open", which is exactly what happens today.
 *
 * Timers are local: contact.getStateDuration() is only valid inside a
 * connect.contact() callback, and re-entering the SDK once a second to read a
 * clock is more fragile than counting from the event we already receive.
 */
export const useConnectCall = () => {
  const [state, setState] = useState<ICallState>(INITIAL_STATE);

  const startedAt = useRef<number | null>(null);
  const heldAt = useRef<number | null>(null);
  const lastHoldActionAt = useRef(0);
  const lastMuteActionAt = useRef(0);

  useCallConnectingEventListener(
    useCallback(() => {
      startedAt.current = Date.now();
      heldAt.current = null;
      setState((prev) => ({
        ...prev,
        phase: 'connecting',
        elapsedSeconds: 0,
        heldSeconds: 0,
        isHeld: false,
        isMuted: false,
        transfer: null,
        failed: false,
        failureStateName: null,
      }));
    }, []),
  );

  useCallStartedEventListener(
    useCallback(() => {
      // Restart the clock: the ring time shown while connecting is not talk time.
      startedAt.current = Date.now();
      setState((prev) => ({ ...prev, phase: 'connected', elapsedSeconds: 0 }));
    }, []),
  );

  useCallEndedEventListener(
    useCallback((event) => {
      heldAt.current = null;
      const { failure } = event.detail;
      setState((prev) => ({
        ...prev,
        phase: 'ended',
        // A ring that never connected has no talk time: the clock was counting
        // the ring, and the wrap-up board labels this value "Talk time".
        elapsedSeconds: prev.phase === 'connected' ? prev.elapsedSeconds : 0,
        isHeld: false,
        heldSeconds: 0,
        isMuted: false,
        transfer: null,
        failed: !!failure,
        failureStateName: failure?.reason ?? null,
      }));
    }, []),
  );

  // One interval for both clocks. Runs only while a contact is live: 'ended'
  // keeps the stopped time readable, and 'idle' — the phase whenever the modal
  // is closed, since the panel never unmounts — would otherwise leave this
  // ticking into the SDK for the app's whole lifetime.
  useEffect(() => {
    if (state.phase === 'idle' || state.phase === 'ended') return undefined;

    // While CONNECTING a clicked Mute is an ARMED mute: Connect rejects
    // muteParticipant until the patient answers, so the SDK snapshot keeps
    // reporting "not muted" and reconciling against it would strip the arming
    // as soon as the holdoff lapses. The panel's own state is the truth during
    // the ring; the container re-applies it on callStarted.
    const isConnectedPhase = state.phase === 'connected';

    const id = window.setInterval(() => {
      // Read the SDK outside the updater: React may invoke an updater more than
      // once, and a state reducer is not the place to call into connect-streams.
      const transfer = getTransferSnapshot();
      const controls = getCallControlSnapshot();
      const now = Date.now();

      // Hold and mute are flipped optimistically on click and rolled back on
      // failure; the snapshot exists for changes the app did NOT make itself —
      // Amazon's automatic customer hold while a transfer dials, a supervisor
      // force-mute, a resume performed by joining a conference. Freshly
      // clicked tiles are left alone for a few seconds (see HOLDOFF above).
      const reconcileHold =
        controls !== null &&
        now - lastHoldActionAt.current > CONTROL_RECONCILE_HOLDOFF_MS;
      const reconcileMute =
        controls !== null &&
        isConnectedPhase &&
        now - lastMuteActionAt.current > CONTROL_RECONCILE_HOLDOFF_MS;

      // Ref writes stay outside the updater for the same re-invocation reason.
      if (reconcileHold && controls) {
        if (controls.isCustomerOnHold && heldAt.current === null) {
          heldAt.current = now;
        } else if (!controls.isCustomerOnHold && heldAt.current !== null) {
          heldAt.current = null;
        }
      }

      setState((prev) => {
        const elapsedSeconds = startedAt.current
          ? Math.floor((now - startedAt.current) / 1000)
          : 0;
        const heldSeconds = heldAt.current
          ? Math.floor((now - heldAt.current) / 1000)
          : 0;
        const isHeld =
          reconcileHold && controls ? controls.isCustomerOnHold : prev.isHeld;
        const isMuted =
          reconcileMute && controls ? controls.isAgentMuted : prev.isMuted;
        if (
          prev.elapsedSeconds === elapsedSeconds &&
          prev.heldSeconds === heldSeconds &&
          prev.isHeld === isHeld &&
          prev.isMuted === isMuted &&
          sameTransfer(prev.transfer, transfer)
        ) {
          return prev;
        }
        return {
          ...prev,
          elapsedSeconds,
          heldSeconds,
          isHeld,
          isMuted,
          transfer,
        };
      });
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [state.phase]);

  // Display only. Polled rather than subscribed because agent.onStateChange
  // fires for routing events we do not surface, and this value is decorative.
  useEffect(() => {
    const read = () => {
      const agent = getAgentState();
      const agentStateName = agent?.name ?? null;
      const isAgentRoutable = agent?.isRoutable ?? false;
      const isErrorState = agent?.isErrorState ?? false;
      // Agent state is global, and with the Amazon CCP UI hidden in an iframe
      // the agent never acknowledges an error state — so one left behind by an
      // earlier failed dial is still parked there when this call ends. Only
      // trust it as this call's reason if Connect entered it at or after the
      // call started, which a state no older than the call itself says. Without
      // this the panel confidently blames a busy line for a call that failed
      // some other way. No start time (already reset) means no attribution: the
      // panel falls back to its generic could-not-connect copy.
      const isReasonFromThisCall =
        startedAt.current !== null &&
        agent?.durationMs != null &&
        agent.durationMs <= Date.now() - startedAt.current;

      setState((prev) => {
        // Backfill the failure reason. onEnded reads the agent state in the
        // same breath as the ENDED event, but Connect occasionally parks the
        // agent in the error state a beat after the contact ends — this poll
        // catches it. Fill-once only: by the time the agent clears the error
        // state the panel must keep showing why the call failed, not the
        // recovery.
        const failureStateName =
          prev.phase === 'ended' &&
          prev.failed &&
          prev.failureStateName === null &&
          isErrorState &&
          isReasonFromThisCall
            ? agentStateName
            : prev.failureStateName;

        if (
          prev.agentStateName === agentStateName &&
          prev.isAgentRoutable === isAgentRoutable &&
          prev.failureStateName === failureStateName
        ) {
          return prev;
        }
        return { ...prev, agentStateName, isAgentRoutable, failureStateName };
      });
    };

    read();
    const id = window.setInterval(read, AGENT_STATE_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  const markHeld = useCallback((isHeld: boolean) => {
    lastHoldActionAt.current = Date.now();
    heldAt.current = isHeld ? Date.now() : null;
    setState((prev) => ({ ...prev, isHeld, heldSeconds: 0 }));
  }, []);

  const markMuted = useCallback((isMuted: boolean) => {
    lastMuteActionAt.current = Date.now();
    setState((prev) => ({ ...prev, isMuted }));
  }, []);

  /**
   * The modal is mounted with destroyOnClose={false}, so without this the next
   * call would open on the previous call's wrap-up board.
   */
  const reset = useCallback(() => {
    startedAt.current = null;
    heldAt.current = null;
    setState((prev) => ({
      ...INITIAL_STATE,
      agentStateName: prev.agentStateName,
      isAgentRoutable: prev.isAgentRoutable,
    }));
  }, []);

  return { callState: state, markHeld, markMuted, reset };
};

export default useConnectCall;
